// The state every form of the sign-in screens keeps (sign in, sign up, recovery, the verification of the
// email): the send in progress, the failure of the system until a new attempt goes well, the wait the service
// asked for and where each of them is shown.
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthOutcome } from './auth-call';
import { useCooldown, useFormAction } from './forms';

/** What a form shows about its last attempt: a failure of the system, or a wait. The attempt is counted. */
export interface Problem {
  message: string;
  attempt: number;
  /** An action that lets the person go on (send the confirmation again, ask for another link). */
  action?: ReactNode;
}

/**
 * The state every form of these screens keeps: the send in progress, the failure of the system (until a new
 * attempt goes well), the wait and the field error that the system sends back.
 */
export function useAuthForm() {
  const { t } = useTranslation();
  const { busy, run } = useFormAction();
  const cooldown = useCooldown();
  const [problem, setProblem] = useState<Problem | null>(null);
  const [waitSeconds, setWaitSeconds] = useState<number | null>(null);
  const [attempts, setAttempts] = useState(0);

  const fail = (message: string, action?: ReactNode) => {
    setAttempts((count) => count + 1);
    setWaitSeconds(null);
    cooldown.clear();
    setProblem({ message, attempt: attempts + 1, action });
  };
  /** Shows a failure that is not about a field. Returns true when the outcome was one. */
  const show = (outcome: AuthOutcome, messages: Partial<Record<AuthOutcome['kind'], string>> = {}) => {
    if (outcome.kind === 'ok') {
      // A new attempt that goes well is what resolves a failure of the system.
      setProblem(null);
      setWaitSeconds(null);
      cooldown.clear();
      return false;
    }
    if (outcome.kind === 'limit') {
      if (outcome.seconds === null) fail(t('forms.limit.unknown'));
      else {
        setProblem(null);
        setWaitSeconds(outcome.seconds);
        cooldown.start(outcome.seconds);
      }
      return true;
    }
    fail(messages[outcome.kind] ?? t('errors.generic'));
    return true;
  };
  return { busy, run, cooldown, problem, waitSeconds, fail, show, setProblem };
}
